import GenericApi from "@/api/genericApi";
import {
  BankAccountQueryKey,
  CryptoAccountQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";
import {
  BankAccountAdmin,
  CryptoAccount,
  CryptoAccountAdmin,
} from "@/types/BankSettings";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { getUser } from "@/utils/localStorage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import { useAppSelector } from "@/store";

import { NETWORK_OPTIONS } from "@/data/data.tsx";
import { Loader } from "@/components";
import { isBitramp } from "@/utils/utils";
import { AnimatePresence, motion } from "framer-motion";

const BankSettingsAdmin: React.FC = () => {

  const queryClient = useQueryClient();


  const [showAddCryptoAccount, setShowAddCryptoAccount] = useState(false);
  const [confirmCryptoModal, setConfirmCryptoModal] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);

  const handleCloseModal = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setConfirmCryptoModal(false);
    }
  };

  const user = getUser();
  const { showSnackBar } = useSnackBarAlert();

  const refresh = useAppSelector((state) => state.refresh.refresh);

  const [editingCryptoWalletId, setEditingCryptoWalletId] = useState<
    number | null
  >(null);
  const [editedWallet, setEditedWallet] = useState<Partial<CryptoAccountAdmin>>(
    {}
  );

  const [editedBankAccount, setEditedBankAccount] = useState<
    Partial<BankAccountAdmin>
  >({});

  const truncateWallet = (wallet: string, visible = 15) => {
    if (!wallet) return "";
    return wallet.length > visible ? `${wallet.slice(0, visible)}...` : wallet;
  };

  const { data: bankAccounts, isLoading: bankAccountsLoading } = useQuery<
    ApiResponse<BankAccountAdmin>
  >({
    queryFn: async () =>
      GenericApi.get<BankAccountAdmin>(ApiRoutes.DEPOSITBANK),
    queryKey: [BankAccountQueryKey, refresh],
  });

  const { data: cryptoAccounts, isLoading: cryptoAccountsLoading } = useQuery<
    ApiResponse<CryptoAccountAdmin[]>
  >({
    queryFn: async () =>
      GenericApi.get<CryptoAccountAdmin[]>(ApiRoutes.DEPOSITWALLET),
    queryKey: [CryptoAccountQueryKey, refresh],
  });

  const addCryptoAccountMutation = useMutation<
    ApiResponse<CryptoAccountAdmin>,
    ApiError<ValidationErrors>,
    Partial<CryptoAccountAdmin>
  >({
    mutationFn: (data) => {
      return GenericApi.post(`${ApiRoutes.DEPOSITWALLET}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CryptoAccountQueryKey] });
      setEditingCryptoWalletId(null);
      setEditedWallet({});
      showSnackBar("Wallet created successfully", "success", {
        vertical: "top",
        horizontal: "right",
      });
    },
  });

  const updateCryptoAccountMutation = useMutation<
    ApiResponse<CryptoAccountAdmin>,
    ApiError<ValidationErrors>,
    Partial<CryptoAccountAdmin>
  >({
    mutationFn: (data) => {
      return GenericApi.put(`${ApiRoutes.DEPOSITWALLET}/${data.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CryptoAccountQueryKey] });
      setEditingCryptoWalletId(null);
      setEditedWallet({});
      showSnackBar("Wallet updated successfully", "success", {
        vertical: "top",
        horizontal: "right",
      });
    },
  });

  const updateBankAccountMutation = useMutation<
    ApiResponse<BankAccountAdmin>,
    ApiError<ValidationErrors>,
    Partial<BankAccountAdmin>
  >({
    mutationFn: (data) => GenericApi.put(`${ApiRoutes.DEPOSITBANK}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BankAccountQueryKey] });
      setEditedBankAccount({});
      showSnackBar("Bank account updated successfully", "success", {
        vertical: "top",
        horizontal: "right",
      });
    },
  });

  const deleteCryptoAccount = useMutation<
    ApiResponse<CryptoAccount>,
    ApiError<ValidationErrors>,
    { id: number }
  >({
    mutationFn: (data) =>
      GenericApi.delete<CryptoAccount>(`${ApiRoutes.DEPOSITWALLET}/${data.id}`),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [CryptoAccountQueryKey] });
    },
  });

  const onSubmitCrypto = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (editedWallet) {
      addCryptoAccountMutation.mutate(editedWallet);
    }
  };

  const onSubmitEditCrypto = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (editedWallet) {
      updateCryptoAccountMutation.mutate(editedWallet);
    }
  };

  const onSubmitEditBank = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (editedBankAccount.address == undefined) {
      editedBankAccount.address = bankAccounts?.data?.address;
    }
    if (editedBankAccount.owner_name == undefined) {
      editedBankAccount.owner_name = bankAccounts?.data?.owner_name;
    }
    if (editedBankAccount.iban == undefined) {
      editedBankAccount.iban = bankAccounts?.data?.iban;
    }
    if (editedBankAccount.swift == undefined) {
      editedBankAccount.swift = bankAccounts?.data?.swift;
    }
    if (editedBankAccount.name == undefined) {
      editedBankAccount.name = bankAccounts?.data?.name;
    }
    if (editedBankAccount) updateBankAccountMutation.mutate(editedBankAccount);
  };

  const [openNetwork, setOpenNetwork] = useState(false);
  const networkRef = useRef<HTMLDivElement>(null);

  const handleSelectNetwork = (value: string) => {
    setEditedWallet({ ...editedWallet, network: value });
    setOpenNetwork(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        networkRef.current &&
        !networkRef.current.contains(event.target as Node)
      ) {
        setOpenNetwork(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedLabelNetwork =
    NETWORK_OPTIONS.find((opt) => opt.value === editedWallet.network)?.label ||
    "Select a network";

  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const currencyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        currencyRef.current &&
        !currencyRef.current.contains(event.target as Node)
      ) {
        setCurrencyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const CURRENCIES = ["BTC", "ETH", "USDC", "USDT"];

  const handleSelectCurrency = (currency: string) => {
    setEditedWallet({ ...editedWallet, currency });
    setCurrencyDropdownOpen(false);
  };

  const selectedLabelCurrency = editedWallet.currency || "Select a currency";

  if (bankAccountsLoading || cryptoAccountsLoading) {
    return <Loader />;
  }

  console.log("bankAccounts", undefined);

  return (
    <div className="Container pshadow rounded-tr-none mx-auto p-8">
      <div className="settings pt-2 mb-3">
        <div className="flex gap-4 items-center mb-10 pl-10">
          <div className="w-fit whitespace-nowrap semi-bold dark-blue Text">
            Bank Account
          </div>
          <div
            className={`w-full h-[1px]  ${
              isBitramp() ? "bg-[#464646]" : "bg-[#f1f1f1]"
            }`}
          ></div>
        </div>
        <div className="">
          <div
            className={`w-full py-2 border-t-[1px]  ${
              isBitramp() ? "border-[#464646]" : "border-[#F1F1F1]"
            }`}
          >
            <div
              className={`flex  border-b-[1px] pb-2 pl-2 ${
                isBitramp() ? "border-[#464646]" : "border-[#F1F1F1]"
              }`}
            >
              <div className="flex gap-10">
                <div className="light-blue medium Subtitle  w-[10rem] flex justify-start items-center gap-1">
                  {bankAccounts?.data?.name}
                </div>
                {/* <div className="bank__account__currency w-[6rem]">
                    {bankAccounts?.data?.currency}
                  </div> */}
                <div className="light-blue medium Subtitle  w-[20rem]">
                  {bankAccounts?.data?.iban}
                </div>
                {/* <div>{bankAccounts?.data?.status.status}</div> */}
              </div>
            </div>

            <Fragment>
              <div className="w-full mt-4">
                <>
                  <div className="flex justify-between items-center">
                    <div className="pt-5 pb-2 Text dark-blue semi-bold w-full ml-10">{`${
                      bankAccounts?.data
                        ? "Edit Bank Account"
                        : "Add Bank Account"
                    }`}</div>
                  </div>
                  <div className="flex justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="95%"
                      height="2"
                      viewBox="0 0 1166 2"
                      fill="none"
                    >
                      <path
                        d="M1 1L1165 0.999898"
                        stroke={`${isBitramp() ? "#464646" : "#E0E0E0"}`}
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <form onSubmit={onSubmitEditBank}>
                    <div className="input__group py-6 px-10 d-flex items-center gap-10 mb-2">
                      <div className="flex items-center gap-10 mb-2">
                        <label
                          htmlFor="address"
                          className={`Text  regular whitespace-nowrap w-[20%] ${
                            isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                          }`}
                        >
                          Bank address
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: "#FFF",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          value={
                            editedBankAccount.address == undefined
                              ? bankAccounts?.data?.address
                              : editedBankAccount.address
                          }
                          onChange={(e) =>
                            setEditedBankAccount({
                              ...editedBankAccount,
                              address: e.target.value,
                            })
                          }
                          className={`bank__input Input ${
                            isBitramp()
                              ? "!bg-[#212121] placeholder:text-[#464646]"
                              : ""
                          }`}
                          id="address"
                          name="address"
                          placeholder="Bank address"
                        />
                      </div>
                      <div className="flex items-center gap-10 mb-2">
                        <label
                          htmlFor="account"
                          className={`Text  regular whitespace-nowrap w-[20%] ${
                            isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                          }`}
                        >
                          Account Owner
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: "#FFF",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          value={
                            editedBankAccount.owner_name == undefined
                              ? bankAccounts?.data?.owner_name
                              : editedBankAccount.owner_name
                          }
                          onChange={(e) =>
                            setEditedBankAccount({
                              ...editedBankAccount,
                              owner_name: e.target.value,
                            })
                          }
                          className={`bank__input Input ${
                            isBitramp()
                              ? "!bg-[#212121] placeholder:text-[#464646]"
                              : ""
                          }`}
                          id="account"
                          name="account"
                          placeholder="Albert John"
                        />
                      </div>
                      <div className="flex items-center gap-10 mb-2">
                        <label
                          htmlFor="iban"
                          className={`Text  regular whitespace-nowrap w-[20%] ${
                            isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                          }`}
                        >
                          Account Number (IBAN)
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: "#FFF",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          value={
                            editedBankAccount.iban == undefined
                              ? bankAccounts?.data?.iban
                              : editedBankAccount.iban
                          }
                          onChange={(e) => {
                            setEditedBankAccount({
                              ...editedBankAccount,
                              iban: e.target.value,
                            });
                          }}
                          className={`bank__input Input ${
                            isBitramp()
                              ? "!bg-[#212121] placeholder:text-[#464646]"
                              : ""
                          }`}
                          id="iban"
                          name="iban"
                          placeholder="RO00BRDE 1267 8987 6453 2145"
                        />
                      </div>
                      <div className="flex items-center gap-10 mb-2">
                        <label
                          htmlFor="bank_name"
                          className={`Text  regular whitespace-nowrap w-[20%] ${
                            isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                          }`}
                        >
                          Bank Name
                        </label>
                        <input
                          type="text"
                          style={{
                            borderRadius: "10px",
                            background: "#FFF",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          value={
                            editedBankAccount.name == undefined
                              ? bankAccounts?.data?.name
                              : editedBankAccount.name
                          }
                          onChange={(e) => {
                            setEditedBankAccount({
                              ...editedBankAccount,
                              name: e.target.value,
                            });
                          }}
                          className={`bank__input Input ${
                            isBitramp()
                              ? "!bg-[#212121] placeholder:text-[#464646]"
                              : ""
                          }`}
                          id="bank_name"
                          name="bank_name"
                          placeholder="Bank Name"
                        />
                      </div>
                      <div className="flex items-center gap-10 mb-2">
                        <label
                          htmlFor="account"
                          className={`Text  regular whitespace-nowrap w-[20%] ${
                            isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                          }`}
                        >
                          SWIFT Code
                        </label>
                        <input
                          type="text"
                          value={
                            editedBankAccount.swift == undefined
                              ? bankAccounts?.data?.swift
                              : editedBankAccount.swift
                          }
                          onChange={(e) => {
                            setEditedBankAccount({
                              ...editedBankAccount,
                              swift: e.target.value,
                            });
                          }}
                          style={{
                            borderRadius: "10px",
                            background: "#FFF",
                            border: "0.5px solid #D9D9D9",
                            width: "90%",
                          }}
                          className={`bank__input Input ${
                            isBitramp()
                              ? "!bg-[#212121] placeholder:text-[#464646]"
                              : ""
                          }`}
                          id="account"
                          name="account"
                          placeholder="SWIFT Code"
                        />
                      </div>
                    </div>
                    <div className="mt-3 ml-7">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="97%"
                        height="2"
                        viewBox="0 0 1166 2"
                        fill="none"
                      >
                        <path
                          d="M1 1L1165 0.999898"
                          stroke={isBitramp() ? "#464646" : "#E0E0E0"}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div className="mt-7 mb-5 ml-7">
                      <button type="submit" className="Primary_button">
                        Save
                      </button>
                    </div>
                  </form>
                </>
              </div>
            </Fragment>
          </div>
        </div>
      </div>

      <div className="settings mb-3 pb-4 pt-12">
        <div className="flex gap-4 items-center mb-10 pl-10">
          <div className="Text dark-blue semi-bold whitespace-nowrap">
            Crypto Wallet
          </div>
          <div
            className={`w-full h-[1px]  ${
              isBitramp() ? "bg-[#464646]" : "bg-[#f1f1f1]"
            }`}
          ></div>
        </div>
        <div className="px-14">
          {cryptoAccounts?.data
            ? cryptoAccounts?.data.map((cryptoAccount) => {
                return (
                  <div key={cryptoAccount.id}>
                    <div
                      className={`flex gap-15 w-full  border-t-2 py-2 ${
                        isBitramp() ? "border-[#464646]" : "border-[#F1F1F1]"
                      }`}
                    >
                      <div className="flex gap-10 items-center">
                        <div
                          className={` medium Subtitle w-[18rem] flex justify-start items-center gap-1 ${
                            isBitramp() ? "text-[#FFF]" : "light-blue"
                          }`}
                        >
                          {cryptoAccount.name}
                        </div>
                        <div
                          className={` semi-bold Subtitle w-[6rem] ${
                            isBitramp() ? "text-[#FFF]" : "light-blue"
                          }`}
                        >
                          {cryptoAccount.currency}
                        </div>
                        <div
                          className={` medium Subtitle w-[15rem] flex justify-center gap-2 items-center ${
                            isBitramp() ? "text-[#FFF]" : "light-blue"
                          }`}
                        >
                          {truncateWallet(cryptoAccount.address)}
                        </div>
                      </div>
                      <div className="ml-auto flex gap-4">
                        <button
                          onClick={() => {
                            setEditingCryptoWalletId(cryptoAccount.id);
                            setEditedWallet({
                              ...cryptoAccount,
                              id: cryptoAccount.id,
                            });
                          }}
                          className={`Outline_button ${
                            isBitramp()
                              ? "!text-[#EE33D9] !underline-offset-4 !border-none"
                              : ""
                          }`}
                        >
                          edit
                        </button>
                        {user?.is_staff ? (
                          <button
                            // onClick={() => {
                            //   deleteCryptoAccount.mutate({
                            //     id: cryptoAccount.id,
                            //   });
                            // }}
                            onClick={() => setShowModal(true)}
                            className={` ${
                              isBitramp()
                                ? "Delete_button_bitramp"
                                : "Outline_red_button"
                            }`}
                          >
                            delete
                          </button>
                        ) : null}
                      </div>
                      {showModal && (
                        <div
                          className="fixed inset-0 flex items-center justify-center bg-black/20 backdrop-blur-[0.1px] z-[999999999]"
                          onClick={() => setShowModal(false)}
                        >
                          <div
                            className={`${
                              isBitramp() ? "bg-[#2b2b2b]" : "bg-white"
                            } min-w-[30rem] h-auto rounded-[1.5rem] text-center relative`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => setShowModal(false)}
                              className="absolute top-4 right-4"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="19"
                                height="19"
                                viewBox="0 0 19 19"
                                fill="none"
                              >
                                <path
                                  d="M17 17L2 2"
                                  stroke={isBitramp() ? "#fff" : "#202A43"}
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                />
                                <path
                                  d="M2 17L17 2"
                                  stroke={isBitramp() ? "#fff" : "#202A43"}
                                  strokeWidth="3"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>

                            <div
                              className={`${
                                isBitramp() ? "text-white" : "text-[#05004E]"
                              } text-xl font-bold pt-12`}
                            >
                              Are you sure you want to delete?
                            </div>

                            <div className="mt-7 mb-8 flex gap-3 justify-center">
                              <button
                                className="Primary_button"
                                onClick={() => {
                                  deleteCryptoAccount.mutate({
                                    id: cryptoAccount.id,
                                  });
                                  setShowModal(false);
                                }}
                              >
                                Yes
                              </button>
                              <button
                                className="Decline_button"
                                onClick={() => setShowModal(false)}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {editingCryptoWalletId === cryptoAccount.id ? (
                      <div
                        className={`w-full bank__add__container mt-4 ${
                          isBitramp() ? "bg-[#313131]" : ""
                        }`}
                      >
                        <>
                          <div className="flex justify-between items-center">
                            <div className="py-4 px-8 Subtitle dark-blue semi-bold w-full">
                              {`Edit ${cryptoAccount.name}`}
                            </div>
                            <svg
                              onClick={() => {
                                setEditingCryptoWalletId(null);
                                setEditedWallet({});
                              }}
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="13"
                              viewBox="0 0 16 13"
                              fill="none"
                              className="mr-9 cursor-pointer"
                            >
                              <path
                                d="M6.12346 6.50889L0.123457 0H3.87654L6.07407 2.32969C6.81482 3.12996 7.4321 3.87688 8.09877 4.76607L12.2963 0H15.9753L10.0741 6.49111L16 13H12.2716L10.2469 10.8482C9.45679 9.97674 8.81481 9.19425 8.09877 8.25171L3.7037 13H0L6.12346 6.50889Z"
                                fill="#9A9A9A"
                              />
                            </svg>
                          </div>
                          <div className="flex justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="95%"
                              height="2"
                              viewBox="0 0 1166 2"
                              fill="none"
                            >
                              <path
                                d="M1 1L1165 0.999898"
                                stroke={`${
                                  isBitramp() ? "#464646" : "#E0E0E0"
                                }`}
                                strokeLinecap="round"
                              />
                            </svg>
                          </div>
                          <form onSubmit={onSubmitEditCrypto}>
                            <div className="input__group py-6 px-8 d-flex items-center gap-10 mb-2">
                              <div className="flex items-center gap-10 mb-2">
                                <label
                                  htmlFor="account"
                                  className={`Text regular whitespace-nowrap w-[20%] ${
                                    isBitramp()
                                      ? "text-[#9F9F9F]"
                                      : " dark-gray"
                                  }`}
                                >
                                  Wallet Name
                                </label>
                                <input
                                  type="text"
                                  value={
                                    editedWallet.name != undefined
                                      ? editedWallet.name
                                      : cryptoAccount.name
                                  }
                                  onChange={(e) => {
                                    setEditedWallet({
                                      ...editedWallet,
                                      name: e.target.value,
                                    });
                                  }}
                                  style={{
                                    borderRadius: "10px",
                                    background: "#FFF",
                                    border: "0.5px solid #D9D9D9",
                                    width: "90%",
                                  }}
                                  className={`bank__input Input ${
                                    isBitramp()
                                      ? "!bg-[#212121] placeholder:text-[#464646]"
                                      : ""
                                  }`}
                                  id="account"
                                  name="account"
                                  placeholder="Wallet Name"
                                />
                              </div>
                              <div className="flex items-center gap-10 mb-2">
                                <label
                                  htmlFor="account"
                                  className={`Text regular whitespace-nowrap w-[20%] ${
                                    isBitramp()
                                      ? "text-[#9F9F9F]"
                                      : " dark-gray"
                                  }`}
                                >
                                  Wallet Address
                                </label>
                                <input
                                  type="text"
                                  value={
                                    editedWallet.address != undefined
                                      ? editedWallet.address
                                      : cryptoAccount.address
                                  }
                                  onChange={(e) => {
                                    setEditedWallet({
                                      ...editedWallet,
                                      address: e.target.value,
                                    });
                                  }}
                                  style={{
                                    borderRadius: "10px",
                                    background: "#FFF",
                                    border: "0.5px solid #D9D9D9",
                                    width: "90%",
                                  }}
                                  className={`bank__input Input ${
                                    isBitramp()
                                      ? "!bg-[#212121] placeholder:text-[#464646]"
                                      : ""
                                  }`}
                                  id="account"
                                  name="account"
                                  placeholder="0xbF766990647e0B3B02Ade99A9e0CD61F4CA1b20f"
                                />
                              </div>
                              <div className="flex items-center gap-10 mb-2">
                                <label
                                  htmlFor="currency"
                                  className={`Text regular whitespace-nowrap w-[17.5%] ${
                                    isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                                  }`}
                                >
                                  Network
                                </label>
                                <div className="relative w-[20%]">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setOpenNetwork((prev) => !prev)
                                    }
                                    className={`w-full text-left px-4 py-2 rounded-[0.625rem] border ${
                                      isBitramp()
                                        ? "bg-[#212121] border-[#2B2B2B]  text-white"
                                        : "bg-white border-gray-300 text-gray-700"
                                    }`}
                                  >
                                    {selectedLabelNetwork}
                                  </button>

                                  <AnimatePresence>
                                    {openNetwork && (
                                      <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.2 }}
                                        className={`absolute top-full left-0 w-full mt-1 rounded-[0.625rem] shadow-md z-10 max-h-60 overflow-y-auto ${
                                          isBitramp()
                                            ? "bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] text-white"
                                            : "bg-white border border-gray-200"
                                        }`}
                                      >
                                        {NETWORK_OPTIONS.map((option) => (
                                          <div
                                            key={option.value}
                                            onClick={() =>
                                              handleSelectNetwork(option.value)
                                            }
                                            className={`px-4 py-2 cursor-pointer ${
                                              isBitramp()
                                                ? "hover:bg-[#262626]"
                                                : "hover:bg-gray-100"
                                            }`}
                                          >
                                            {option.label}
                                          </div>
                                        ))}
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              </div>
                              <div className="flex items-center gap-10 mb-2">
                                <label
                                  htmlFor="currency"
                                  className={`Text regular whitespace-nowrap w-[17.5%] ${
                                    isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                                  }`}
                                >
                                  Currency
                                </label>
                                <div className="relative w-[20%]">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setCurrencyDropdownOpen((prev) => !prev)
                                    }
                                    className={`w-full text-left px-4 py-2 rounded-[0.625rem] border ${
                                      isBitramp()
                                        ? "bg-[#212121] border-[#2B2B2B] text-white"
                                        : "bg-white border-gray-300 text-gray-700"
                                    }`}
                                  >
                                    {selectedLabelCurrency}
                                  </button>

                                  <AnimatePresence>
                                    {currencyDropdownOpen && (
                                      <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.2 }}
                                        className={`absolute top-full left-0 w-full mt-1 rounded-[0.625rem] shadow-md z-10 max-h-60 overflow-y-auto ${
                                          isBitramp()
                                            ? "bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] text-white"
                                            : "bg-white border border-gray-200"
                                        }`}
                                      >
                                        {CURRENCIES.map((currency) => (
                                          <div
                                            key={currency}
                                            onClick={() =>
                                              handleSelectCurrency(currency)
                                            }
                                            className={`px-4 py-2 cursor-pointer ${
                                              isBitramp()
                                                ? "hover:bg-[#262626]"
                                                : "hover:bg-gray-100"
                                            }`}
                                          >
                                            {currency}
                                          </div>
                                        ))}
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 ml-8 mb-7">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="97%"
                                height="2"
                                viewBox="0 0 1166 2"
                                fill="none"
                              >
                                <path
                                  d="M1 1L1165 0.999898"
                                  stroke="#E0E0E0"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                            <div className="mt-7 flex justify-between items-center mb-5 ml-7">
                              <button
                                type="submit"
                                className="Primary_button mb-4"
                              >
                                Save
                              </button>
                            </div>
                          </form>
                        </>
                      </div>
                    ) : null}
                  </div>
                );
              })
            : null}
          <div
            onClick={() => {
              setShowAddCryptoAccount(true);
              setEditedWallet({});
            }}
            className={`Text bold  bank__add mt-[1rem] p-4 cursor-pointer text-left ${
              isBitramp() ? "!bg-[#313131] !text-[#8AFEFD]" : "dark-gray"
            }`}
          >
            <p className="">+ Add Crypto Wallet</p>
          </div>

          <div
            className={`w-full bank__add__container mt-4 ${
              isBitramp() ? "bg-[#313131]" : ""
            }`}
          >
            {showAddCryptoAccount ? (
              <>
                <div className="flex justify-between items-center">
                  <div
                    className={`p-5 Text bold w-full ml-5 ${
                      isBitramp() ? "text-[#FFF]" : ""
                    }`}
                  >
                    Add
                  </div>
                  <svg
                    onClick={() => {
                      setShowAddCryptoAccount(false);
                      setEditedWallet({});
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="13"
                    viewBox="0 0 16 13"
                    fill="none"
                    className="mr-9 cursor-pointer"
                  >
                    <path
                      d="M6.12346 6.50889L0.123457 0H3.87654L6.07407 2.32969C6.81482 3.12996 7.4321 3.87688 8.09877 4.76607L12.2963 0H15.9753L10.0741 6.49111L16 13H12.2716L10.2469 10.8482C9.45679 9.97674 8.81481 9.19425 8.09877 8.25171L3.7037 13H0L6.12346 6.50889Z"
                      fill={`#9A9A9A`}
                    />
                  </svg>
                </div>
                <div className="flex justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="95%"
                    height="2"
                    viewBox="0 0 1166 2"
                    fill="none"
                  >
                    <path
                      d="M1 1L1165 0.999898"
                      stroke={`${isBitramp() ? "#464646" : "#e0e0e0"}`}
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <form onSubmit={onSubmitCrypto}>
                  <div className="input__group py-6 px-8 d-flex items-center gap-10 mb-2">
                    <div className="flex items-center gap-10 mb-2">
                      <label
                        htmlFor="account"
                        className={`Text regular whitespace-nowrap w-[20%] ${
                          isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                        }`}
                      >
                        Wallet Name
                      </label>
                      <input
                        type="text"
                        value={editedWallet.name}
                        onChange={(e) => {
                          setEditedWallet({
                            ...editedWallet,
                            name: e.target.value,
                          });
                        }}
                        style={{
                          borderRadius: "10px",
                          background: "#FFF",
                          border: "0.5px solid #D9D9D9",
                          width: "90%",
                        }}
                        className={`bank__input Input ${
                          isBitramp()
                            ? "!bg-[#212121] placeholder:text-[#464646]"
                            : ""
                        }`}
                        id="account"
                        name="account"
                        placeholder="Wallet Name"
                      />
                    </div>
                    <div className="flex items-center gap-10 mb-2">
                      <label
                        htmlFor="account"
                        className={`Text regular whitespace-nowrap w-[20%] ${
                          isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                        }`}
                      >
                        Wallet Address
                      </label>
                      <input
                        type="text"
                        value={editedWallet.address}
                        onChange={(e) => {
                          setEditedWallet({
                            ...editedWallet,
                            address: e.target.value,
                          });
                        }}
                        style={{
                          borderRadius: "10px",
                          background: "#FFF",
                          border: "0.5px solid #D9D9D9",
                          width: "90%",
                        }}
                        className={`bank__input Input ${
                          isBitramp()
                            ? "!bg-[#212121] placeholder:text-[#464646]"
                            : ""
                        }`}
                        id="account"
                        name="account"
                        placeholder="0xbF766990647e0B3B02Ade99A9e0CD61F4CA1b20f"
                      />
                    </div>
                    <div className="flex items-center gap-10 mb-2">
                      <label
                        htmlFor="currency"
                        className={`Text regular whitespace-nowrap w-[17.5%] ${
                          isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                        }`}
                      >
                        Network
                      </label>
                      <div className="relative w-[20%]" ref={networkRef}>
                        <button
                          type="button"
                          onClick={() => setOpenNetwork((prev) => !prev)}
                          className={`w-full text-left px-4 py-2 rounded-[0.625rem] border ${
                            isBitramp()
                              ? "bg-[#212121] border-[#2b2b2b] text-white"
                              : "bg-white border-gray-300 text-gray-700"
                          }`}
                        >
                          {selectedLabelNetwork}
                        </button>

                        <AnimatePresence>
                          {openNetwork && (
                            <motion.div
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                              transition={{ duration: 0.2 }}
                              className={`absolute top-full left-0 w-full mt-1 rounded-[0.625rem] shadow-md z-10 max-h-60 overflow-y-auto ${
                                isBitramp()
                                  ? "bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] text-white"
                                  : "bg-white border border-gray-200"
                              }`}
                            >
                              {NETWORK_OPTIONS.map((option) => (
                                <div
                                  key={option.value}
                                  onClick={() =>
                                    handleSelectNetwork(option.value)
                                  }
                                  className={`px-4 py-2 cursor-pointer ${
                                    isBitramp()
                                      ? "hover:bg-[#262626]"
                                      : "hover:bg-gray-100"
                                  }`}
                                >
                                  {option.label}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                    <div className="flex items-center gap-10 mb-2">
                      <label
                        htmlFor="currency"
                        className={`Text regular whitespace-nowrap w-[17.5%] ${
                          isBitramp() ? "text-[#9F9F9F]" : "dark-gray"
                        }`}
                      >
                        Currency
                      </label>
                      <div className="relative w-[20%]" ref={currencyRef}>
                        <button
                          type="button"
                          onClick={() =>
                            setCurrencyDropdownOpen((prev) => !prev)
                          }
                          className={`w-full text-left px-4 py-2 rounded-[0.625rem] border ${
                            isBitramp()
                              ? "bg-[#212121] border-[#2b2b2b] text-white"
                              : "bg-white border-gray-300 text-gray-700"
                          }`}
                        >
                          {selectedLabelCurrency}
                        </button>

                        <AnimatePresence>
                          {currencyDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                              transition={{ duration: 0.2 }}
                              className={`absolute top-full left-0 w-full mt-1 rounded-[0.625rem] shadow-md z-10 max-h-60 overflow-y-auto ${
                                isBitramp()
                                  ? "bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] text-white"
                                  : "bg-white border border-gray-200"
                              }`}
                            >
                              {CURRENCIES.map((currency) => (
                                <div
                                  key={currency}
                                  onClick={() => handleSelectCurrency(currency)}
                                  className={`px-4 py-2 cursor-pointer ${
                                    isBitramp()
                                      ? "hover:bg-[#262626]"
                                      : "hover:bg-gray-100"
                                  }`}
                                >
                                  {currency}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 ml-8 mb-7">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="97%"
                      height="2"
                      viewBox="0 0 1166 2"
                      fill="none"
                    >
                      <path
                        d="M1 1L1165 0.999898"
                        stroke={`${isBitramp() ? "#464646" : "#e0e0e0"}`}
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="mt-7 mb-5 ml-7">
                    <button
                      type="button"
                      onClick={() => {
                        setEditedWallet({
                          ...editedWallet,
                          id: 0,
                        });
                        setConfirmCryptoModal(true);
                      }}
                      className="mb-4"
                    >
                      <div className="Primary_button">Save</div>
                    </button>
                  </div>
                  {confirmCryptoModal && (
                    <div
                      onClick={handleCloseModal}
                      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full"
                    >
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className={`${
                          isBitramp() ? "bg-[#2b2b2b]" : "bg-white border"
                        } w-[30rem] h-auto  rounded-[1.5rem] text-center relative`}
                      >
                        <button
                          className="absolute top-4 right-4"
                          onClick={() => setConfirmCryptoModal(false)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="19"
                            height="19"
                            viewBox="0 0 19 19"
                            fill="none"
                          >
                            <path
                              d="M17 17L2 2"
                              stroke={isBitramp() ? "#fff" : "#202A43"}
                              stroke-width="3"
                              stroke-linecap="round"
                            />
                            <path
                              d="M2 17L17 2"
                              stroke={isBitramp() ? "#fff" : "#202A43"}
                              stroke-width="3"
                              stroke-linecap="round"
                            />
                          </svg>
                        </button>

                        <div
                          className={`${
                            isBitramp() ? "text-white" : "text-[#05004E]"
                          }  pt-[2.5rem] text-center text-[1.5rem] font-[700] font-satoshi mt-[2rem] px-[2rem]`}
                        >
                          Please Confirm Your Details
                        </div>

                        <div className="light-blue text-[1.2rem] px-[3rem]  pt-[1rem] font-[500] text-center font-satoshi ">
                          I confirm that the information provided is correct and
                          understand that any mistakes may affect future
                          transactions
                        </div>
                        <button
                          type="submit"
                          className={`Primary_button ml-[12rem] my-8`}
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankSettingsAdmin;
